import { writable } from 'svelte/store';
import collect from 'collect.js';
import { uid } from 'uid';
// Models
import Result from '@/Models/Result.js';
// Constants
import { BetType, BetTypePositions, ExoticBetTypes } from '@/Constants/Bet.js';
import { Products } from '@/Constants/RaceProducts.js';
// Stores
import ResultStore from '@/Stores/Racing/ResultStore.js';
import PriceStore from '@/Stores/Racing/PriceStore.js';
// Common
import { formatSelections, isExotic } from '@/Common/ExoticBets.js';
import SelectionStore from '@/Stores/Racing/SelectionStore.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;
	let _resultStore;
	let _priceStore;
	let _selectionStore;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		setupResults(placesPaid = 3, race, fillResults = true, fillExoticResults = true) {
			let placeResults = collect(fillResults ? _resultStore.placings : []);
			let exoticResults = collect(fillExoticResults ? _resultStore.exotics : []);
			let exoticSelections = exoticResults.pluck('selections').collapse().unique('selection_id');
			update((results) => {
				results = collect();
				let duplicatePosition = false;
				// Placings
				[1, 2, 3, 4].forEach((position) => {
					if (duplicatePosition !== false && position > duplicatePosition) {
						position = position + 1;
					}
					let previousPositionResult = placeResults.where('position', position).sortBy('position');
					let exoticPositionSelection = exoticSelections.where('position', position);
					// Check if there is an existing duplicate position.
					if (previousPositionResult.count() > 1) {
						duplicatePosition = position;
					}
					// if we have previous results, use these for this position
					if (previousPositionResult.count()) {
						let count = 0;
						previousPositionResult.each((result) => {
							results.push(transformExistingPlaceResult(result, count > 0 ? position + '-' + count : false));
							if (count > 0 && exoticPositionSelection.count() == 0) {
								let exotics = addEmptyExoticResultsPosition(position.toString(), position + '-' + count);
								exotics.forEach((exotic) => {
									results.push(exotic);
								});
							}
							count++;
						});

						// we don't always get 3rd and 4th position from previous results, so add it based on exotics if we can
					} else if (position >= placesPaid && exoticPositionSelection.count()) {
						exoticPositionSelection.each((row, i) => {
							let result = buildResultForPosition(position.toString());
							result.duplicate = i > 0 ? position + '-' + i.toString() : false;
							result.selection_ids = [row.selection_id.toString()];
							result.selections = [
								{
									selection_id: parseInt(row.selection_id),
									selection_number: parseInt(row.selection_number),
									position: parseInt(result.position)
								}
							];
							results.push(result);
						});
						// NO previous results, setup empty position result
					} else {
						const positionSelection = _selectionStore.where('race_id', race.id).where('position', position);
						if (positionSelection.count() > 0) {
							results.push(buildResultForSelection(position.toString(), positionSelection));
						} else {
							results.push(buildResultForPosition(position.toString()));
						}
					}
				});

				// Exotics
				[BetType.Quinella, BetType.Exacta, BetType.Trifecta, BetType.FirstFour].forEach((exotic) => {
					let previousExoticResult = exoticResults.where('bet_type.id', exotic).sortBy('product.id');
					if (previousExoticResult.count()) {
						let exoticResult = transformExistingExoticResult(previousExoticResult);
						exoticResult.forEach((result) => {
							results.push(result);
						});
					} else {
						results.push(buildResultForExotic(exotic));
					}
				});

				if (duplicatePosition !== false) {
					results = results.each((result) => {
						for (let selection of result.selections) {
							if (selection.position > duplicatePosition && !selection.duplicateHandled) {
								selection['duplicateHandled'] = true;
							}
						}
						return result;
					});
				}

				return results;
			});

			// update exotic selections from any previous selections we retrieved
			this.updateExoticSelections();
		},

		clearExotics() {
			update((results) => {
				results = results.reject((result) => {
					return [BetType.Quinella, BetType.Exacta, BetType.Trifecta, BetType.FirstFour].includes(result.bet_type_id);
				});

				// Exotics
				[BetType.Quinella, BetType.Exacta, BetType.Trifecta, BetType.FirstFour].forEach((exotic) => {
					results.push(buildResultForExotic(exotic));
				});

				results = results.each((result) => {
					if (result.duplicate !== false) {
						let exotics = addEmptyExoticResultsPosition(result.position.toString(), result.duplicate);
						exotics.forEach((exotic) => {
							results.push(exotic);
						});
					}
					return result;
				});

				return results;
			});

			// update exotic selections from any previous selections we retrieved
			this.updateExoticSelections();
		},

		saveResults(race) {
			const result = new Result();
			result.resourceUrl = () => {
				return `${result.baseUrl()}/races/${race.id}/results`;
			};

			return result.saveManual(preparePayloadToSend()).then((results) => {
				return results;
			});
		},

		updateResultSelection(resultId, selectionId, selectionNumber) {
			update((results) => {
				return results.each((result) => {
					if (result.id === resultId) {
						if (parseInt(selectionId)) {
							result.selection_ids = [selectionId];
							result.selections = [
								{
									selection_id: parseInt(selectionId),
									selection_number: parseInt(selectionNumber),
									position: parseInt(result.position)
								}
							];
						} else {
							result.selection_ids = [];
							result.selections = [];
						}
					}
					return result;
				});
			});

			this.updateExoticSelections();
		},

		updateDividendsFromSelection(place, selectionId) {
			let prices = _priceStore.where('selection_id', selectionId);
			let dividends = place.dividends.map((dividend) => {
				dividend.value = prices
					.where('product.id', dividend.product_id)
					.where('bet_type.id', dividend.bet_type_id)
					.pluck('price')
					.first();
				return dividend;
			});
			update((results) => {
				return results.map((result) => {
					if (result.id === place.id) {
						result.dividends = dividends;
					}
					return result;
				});
			});
		},

		updateExoticSelections() {
			update((results) => {
				return results.map((result) => {
					if (!isExotic(result.bet_type_id)) return result;

					let selectionIds = [...result.selection_ids];
					let selections = [];
					let storeSelections = _store
						.where('bet_type_id', BetType.Place)
						.where('position', '<=', BetTypePositions[result.bet_type_id])
						.sortBy('position');
					if (storeSelections) {
						selectionIds = buildSelectionsForExotic(
							'selection_ids.0',
							storeSelections,
							result.duplicate,
							result.bet_type_id
						);
						selections = buildSelectionsForExotic(
							'selections.0',
							storeSelections,
							result.duplicate,
							result.bet_type_id
						);
					}
					result.selection_ids = selectionIds.map((i) => i.toString());
					result.selections = selections;
					result.result_string = formatExoticResults(selections, result.bet_type_id);

					return result;
				});
			});
		},

		addEmptyResultForPosition(position) {
			update((results) => {
				// TODO: currently enforce only 1 tied position as we need too many permutations for multi tied/duplicate per position
				if (results.where('duplicate', '!==', false).count() > 0) return results;

				let duplicate = `${position}-${_store.where('position', position).count()}`;
				results.push(buildResultForPosition(position, duplicate));
				let exotics = addEmptyExoticResultsPosition(position, duplicate);
				exotics.forEach((exotic) => {
					results.push(exotic);
				});

				results = results.each((result) => {
					if (result.position > position) {
						result.position = (parseInt(result.position.toString()) + 1).toString();
					}
					for (let selection of result.selections) {
						if (selection.position > position && !selection.duplicateHandled) {
							selection.position = selection.position + 1;
							selection['duplicateHandled'] = true;
						}
					}
					return result;
				});

				this.updateExoticSelections();

				return results;
			});
		},

		removeResultForId(id, position) {
			update((results) => {
				// we want to remove any exotic results added by this duplicate place result
				results = results.reject((result) => result.id === id || result.duplicate !== false);
				results = results.each((result) => {
					if (result.position > position) {
						result.position = (parseInt(result.position.toString()) - 1).toString();
					}
					for (let selection of result.selections) {
						if (selection.position > position && selection.duplicateHandled) {
							selection.position = selection.position - 1;
							delete selection.duplicateHandled;
						}
					}
					return result;
				});

				return results;
			});
			this.updateExoticSelections();
		}
	};

	/**
	 * PRIVATE METHODS
	 * @param {string} position
	 * @param {boolean|string} duplicate
	 */
	function buildResultForPosition(position, duplicate = false) {
		let dividends = buildDividendForBetType(BetType.Place);
		if (position === '1') {
			dividends = buildDividendForBetType(BetType.Win).concat(dividends);
		}
		return {
			id: uid(),
			duplicate,
			selection_ids: [],
			selections: [],
			position,
			dividends,
			bet_type_id: BetType.Place
		};
	}

	function buildResultForSelection(position, selections, duplicate = false) {
		let dividends = buildDividendForBetType(BetType.Place);
		if (position === '1') {
			dividends = buildDividendForBetType(BetType.Win).concat(dividends);
		}
		let selectionsObject = [];
		selections.each((selection) => {
			selectionsObject.push({
				selection_id: selection.id,
				selection_number: selection.number,
				position: selection.position
			});
		});

		return {
			id: uid(),
			duplicate,
			selection_ids: selections.pluck('id').all(),
			selections: selectionsObject,
			position,
			dividends,
			bet_type_id: BetType.Place
		};
	}

	function buildResultForExotic(exotic, duplicate = false) {
		let dividends = buildDividendForBetType(exotic);
		return {
			id: uid(),
			duplicate,
			selection_ids: [],
			selections: [],
			dividends,
			bet_type_id: exotic,
			result_string: '-'
		};
	}

	function buildDividendForBetType(betTypeId) {
		let dividends = [];
		[Products.NSW, Products.SUP, Products.TATTS].forEach((product) => {
			let dividend = {
				product_id: product,
				bet_type_id: betTypeId,
				value: isExotic(betTypeId) ? null : 0
			};
			if (isExotic(betTypeId)) dividend['prize_pool'] = null;
			dividends.push(dividend);
		});
		return dividends;
	}

	/**
	 * This function gets used for both selection_ids and selections
	 * Little clunky, but basically it builds up the correct selections
	 * with the position order required with duplicates
	 */
	function buildSelectionsForExotic(key = 'selections.0', results, duplicate = false, betTypeId) {
		let selections = [];
		results.groupBy('position').each((result) => {
			let getSelection;
			if (result.count() > 1) {
				let multi = handleDuplicate(result, duplicate, betTypeId);
				if (key === 'selections.0') {
					multi
						.sortBy('position')
						.toArray()
						.forEach((i) => selections.push(i));
				} else {
					multi
						.collapse()
						.sortBy('position')
						.pluck('selection_id')
						.toArray()
						.forEach((i) => selections.push(i));
				}
			} else {
				getSelection = result.pluck(key).first();
				if (getSelection) {
					selections.push(getSelection);
				}
			}
		});

		return selections;
	}

	/**
	 * As each position results selections will be the same position for a duplicate result,
	 * we need to have them incrementing, so it builds our result string correctly
	 * simplified e.g. [{sel: 1, position:1},{sel: 2, position:1}] => [{sel: 1, position:1},{sel: 2, position:2}]
	 */
	function handleDuplicate(data, duplicate, betTypeId) {
		const exoticPositionMap = {
			[BetType.Quinella]: '2',
			[BetType.Exacta]: '2',
			[BetType.Trifecta]: '3',
			[BetType.FirstFour]: '4'
		};
		let remove = data.pluck('position').first() >= exoticPositionMap[betTypeId];
		let results = collect(JSON.parse(JSON.stringify(data)));
		let group = duplicate ? results.where('duplicate', '===', false) : results.where('duplicate', '!==', false);
		group.map((result) => {
			if (!remove && result.selections.length && duplicate) {
				result.selections[0].position = result.selections[0].position + 1;
			} else if (!remove && result.selections.length && !duplicate) {
				result.selections[0].position = result.selections[0].position + 1;
			} else if (remove && result.selections.length) {
				result.selections.shift();
			}
			return result;
		});

		return results.pluck('selections').collapse();
	}

	function addEmptyExoticResultsPosition(position, duplicate) {
		let results = [];
		switch (position) {
			case '1':
			case '2':
				if (position == '2' && duplicate !== false) {
					results.push(buildResultForExotic(BetType.Quinella, duplicate));
				}
				results.push(buildResultForExotic(BetType.Exacta, duplicate));
				results.push(buildResultForExotic(BetType.Trifecta, duplicate));
				results.push(buildResultForExotic(BetType.FirstFour, duplicate));
				break;

			case '3':
				results.push(buildResultForExotic(BetType.Trifecta, duplicate));
				results.push(buildResultForExotic(BetType.FirstFour, duplicate));
				break;

			case '4':
				results.push(buildResultForExotic(BetType.FirstFour, duplicate));
				break;

			default:
				break;
		}

		return results;
	}

	/**
	 * The current Result API response does not match the new manual result object
	 * This might be redundant in the future when updated on the API result response
	 * @param {*} result
	 * @param {*} duplicate
	 */
	function transformExistingPlaceResult(result, duplicate = false) {
		let transformed = {
			id: uid(),
			duplicate: duplicate ? duplicate : false,
			selection_ids: [result.selection_id.toString()],
			selections: [
				{
					selection_id: result.selection_id,
					selection_number: result.selection_number,
					position: result.position
				}
			],
			position: result.position.toString(),
			dividends:
				result.position.toString() === '1'
					? buildDividendForBetType(BetType.Win).concat(buildDividendForBetType(BetType.Place))
					: buildDividendForBetType(BetType.Place),
			bet_type_id: BetType.Place
		};

		result.dividends.sort((a, b) => a.bet_type.id - b.bet_type.id);
		result.dividends.forEach((dividend) => {
			for (let transformedDividends of transformed.dividends) {
				if (
					transformedDividends.product_id == dividend.product.id &&
					transformedDividends.bet_type_id == dividend.bet_type.id
				) {
					transformedDividends.value = dividend.value;
					break;
				}
			}
		});

		return transformed;
	}

	/**
	 * The current Result API response does not match the new manual result object
	 * This might be redundant in the future when updated on the API result response
	 * @param {*} result
	 */
	function transformExistingExoticResult(result) {
		let results = collect();
		let productGroups = result.groupBy('product.id');
		let group = productGroups.first();
		let count = 0;
		// create our single result object (+ duplicates if tied)
		group.each((g) => {
			let selection_ids = [];
			g.selections.forEach((selection) => {
				selection_ids.push(selection.selection_id.toString());
			});
			results.push({
				id: uid(),
				duplicate: count > 0 ? count.toString() : false,
				selection_ids: selection_ids,
				selections: g.selections,
				dividends: buildDividendForBetType(g.bet_type.id),
				bet_type_id: g.bet_type.id,
				result_string: formatExoticResults(g.selections, g.bet_type.id)
			});
			count++;
		});

		// add the dividends for each product per result
		productGroups.each((group) => {
			group.each((g) => {
				let dividends = results
					.where('bet_type_id', g.bet_type.id)
					.where('result_string', formatExoticResults(g.selections, g.bet_type.id))
					.pluck('dividends')
					.first();
				if (dividends) {
					dividends.map((div) => {
						if (div.product_id === g.product.id) {
							div.value = g.value;
							div.prize_pool = g.prize_pool;
						}
						return div;
					});
				}
			});
		});

		return results.toArray();
	}

	function formatExoticResults(selections, bet_type_id) {
		let result = [];
		selections.forEach((selection) => {
			result.push({ number: selection.selection_number, position: selection.position });
		});
		return result.length ? formatSelections(result, bet_type_id) : '-';
	}

	function preparePayloadToSend() {
		let payload = JSON.parse(_store.toJson());
		payload = payload.filter((result) => {
			if (result.position && parseInt(result.position.toString()) > 4) {
				return false;
			}
			// remove the props we added for convenience cause BE doesn't care about them!
			['id', 'bet_type_id', 'duplicate', 'result_string', 'selections'].forEach((remove) => {
				delete result[remove];
			});

			// make sure the dividend has a int value! 0 for unset and >= 1 (100) for set!
			for (const dividend of result.dividends) {
				dividend.value = parseInt(dividend.value);
				if (isNaN(dividend.value) || dividend.value === null) {
					dividend.value = 0;
				}
				if (ExoticBetTypes.includes(dividend.bet_type_id)) {
					dividend.prize_pool = parseInt(dividend.prize_pool);
					if (isNaN(dividend.prize_pool) || dividend.prize_pool === null) {
						dividend.prize_pool = 0;
					}
				}
			}

			// convert our strings to int's to make it nice
			if (result.position) result.position = parseInt(result.position);
			result.selection_ids = result.selection_ids.map((i) => parseInt(i));

			// we only want a result if it has has a selection added to the placings
			return result.selection_ids.length;
		});

		return { results: payload };
	}

	ResultStore.subscribe((value) => {
		_resultStore = value;
	});

	PriceStore.subscribe((value) => {
		_priceStore = value;
	});

	SelectionStore.subscribe((value) => {
		_selectionStore = value;
	});

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
